import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { RefreshCw, CheckCircle2, AlertCircle, XCircle, Activity, Clock, Database, Users, BookOpen, Layers, BellOff, Bell, Wrench } from 'lucide-react';
import api from '@/lib/api';

const fmtMs = (ms) => ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtSR = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSec = (s) => { if (s == null) return '—'; if (s < 60) return `${s}s`; if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`; return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`; };

const STATUS_TONE = {
  healthy: { cls: 'text-success bg-success/10 border-success/20', icon: CheckCircle2, label: 'Healthy' },
  warning: { cls: 'text-warning bg-warning/10 border-warning/20', icon: AlertCircle, label: 'Warning' },
  error: { cls: 'text-error bg-error/10 border-error/20', icon: XCircle, label: 'Error' },
  unknown: { cls: 'text-muted-foreground bg-muted/30 border-border', icon: AlertCircle, label: 'Unknown' },
  mixed: { cls: 'text-warning bg-warning/5 border-warning/20', icon: AlertCircle, label: 'Mixed' },
};

function StatusBadge({ status, testid }) {
  const t = STATUS_TONE[status] || STATUS_TONE.unknown;
  const Icon = t.icon;
  return (
    <Badge variant="outline" className={`gap-1.5 text-[10px] font-semibold uppercase tracking-wider ${t.cls}`} data-testid={testid}>
      <Icon size={10} /> {t.label}
    </Badge>
  );
}

function Section({ title, icon: Icon, status, children, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon size={14} className="text-muted-foreground" />
          <h3 className="text-sm font-bold text-foreground">{title}</h3>
          <span className="ml-auto"><StatusBadge status={status} testid={`${testid}-status`} /></span>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function ERPHealthReportPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mute, setMute] = useState({ muted: false });
  const [backfill, setBackfill] = useState(null);
  const [bfLoading, setBfLoading] = useState(false);

  const runBackfill = async (apply) => {
    setBfLoading(true);
    try {
      const { data } = await api.post(`/admin/backfill-account-codes?apply=${apply}`);
      setBackfill(data);
      const n = apply ? data.lines_fixed : data.lines_to_fix;
      if (apply) toast.success(`Fixed ${n} journal lines across ${data.journal_entries_updated} entries`);
      else toast.info(`Dry run: ${n} lines would be fixed, ${data.unmatched_count} need manual mapping`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally { setBfLoading(false); }
  };

  const [rebalance, setRebalance] = useState(null);
  const [rbLoading, setRbLoading] = useState(false);
  const runRebalance = async (apply) => {
    setRbLoading(true);
    try {
      const { data } = await api.post(`/admin/fix-unbalanced-entries?apply=${apply}`);
      setRebalance(data);
      const n = apply ? data.entries_fixed : data.entries_to_fix;
      if (apply) toast.success(`Rebalanced ${n} journal entries`);
      else toast.info(`Dry run: ${n} entries would be rebalanced, ${data.entries_skipped_ambiguous} need manual review`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rebalance failed');
    } finally { setRbLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        api.get('/admin/erp-health-report'),
        api.get('/admin/mute-health-alerts').catch(() => ({ data: { muted: false } })),
      ]);
      setReport(r.data);
      setMute(m.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ERP health report');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleMute = async () => {
    try {
      if (mute.muted) {
        await api.delete('/admin/mute-health-alerts');
        toast.success('Health alerts resumed');
      } else {
        await api.post('/admin/mute-health-alerts', { hours: 24, reason: 'Muted from ERP Health Report page' });
        toast.success('Health alerts muted for 24h');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle mute');
    }
  };

  const s = report?.sections || {};

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-[1200px] mx-auto" data-testid="erp-health-report-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-200/50">
              <Activity size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">ERP Health Report</h1>
              <p className="text-sm text-muted-foreground mt-0.5">One-shot platform audit — backend · cold-start · reconciliation · DB · integrity</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {report && <StatusBadge status={report.overall} testid="overall-status" />}
            <Button
              variant={mute.muted ? 'default' : 'outline'}
              size="sm"
              onClick={toggleMute}
              className={mute.muted ? 'bg-warning/90 hover:bg-warning text-warning-foreground' : ''}
              data-testid="mute-toggle-btn"
              title={mute.muted ? `Muted until ${new Date(mute.mute_until).toLocaleString()}` : 'Silence alerts for 24h'}
            >
              {mute.muted ? <><Bell size={14} className="mr-1" /> Unmute</> : <><BellOff size={14} className="mr-1" /> Mute 24h</>}
            </Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-btn">
              <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Maintenance: Backfill account codes onto legacy journal entries */}
        <Card data-testid="backfill-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="max-w-2xl">
                <h3 className="font-semibold text-foreground text-sm flex items-center gap-2"><Wrench size={15} className="text-teal-600" /> Backfill Account Codes</h3>
                <p className="text-xs text-muted-foreground mt-1">Re-maps legacy journal-entry lines to the correct Chart-of-Accounts code (derived from the account name). Makes Trial Balance, General Ledger & Balance Sheet drill-downs reconcile by code. Safe & idempotent — preview first.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => runBackfill(false)} disabled={bfLoading} data-testid="backfill-preview-btn">
                  <RefreshCw size={14} className={`mr-1 ${bfLoading ? 'animate-spin' : ''}`} /> Preview
                </Button>
                <Button size="sm" onClick={() => runBackfill(true)} disabled={bfLoading || !backfill || (backfill.lines_to_fix === 0 && !backfill.applied)} data-testid="backfill-apply-btn" className="bg-teal-600 hover:bg-teal-700">
                  Apply Fixes
                </Button>
              </div>
            </div>
            {backfill && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center" data-testid="backfill-result">
                <div className="rounded-lg bg-muted/50 py-2"><p className="text-lg font-bold text-foreground">{backfill.lines_scanned}</p><p className="text-[10px] text-muted-foreground uppercase">Scanned</p></div>
                <div className="rounded-lg bg-emerald-50 py-2"><p className="text-lg font-bold text-emerald-600">{backfill.applied ? backfill.lines_fixed : backfill.lines_to_fix}</p><p className="text-[10px] text-muted-foreground uppercase">{backfill.applied ? 'Fixed' : 'To Fix'}</p></div>
                <div className="rounded-lg bg-muted/50 py-2"><p className="text-lg font-bold text-foreground">{backfill.journal_entries_updated}</p><p className="text-[10px] text-muted-foreground uppercase">Entries Updated</p></div>
                <div className="rounded-lg bg-amber-50 py-2"><p className="text-lg font-bold text-amber-600">{backfill.unmatched_count}</p><p className="text-[10px] text-muted-foreground uppercase">Need Manual Map</p></div>
              </div>
            )}
            {backfill?.unmatched_names?.length > 0 && (
              <p className="text-[11px] text-muted-foreground mt-2">Unmatched (map these manually in Category Mapping): {backfill.unmatched_names.slice(0, 8).map(u => `${u.name} (${u.lines})`).join(', ')}</p>
            )}

            <div className="mt-4 pt-3 border-t border-border flex items-start justify-between flex-wrap gap-3">
              <div className="max-w-2xl">
                <h3 className="font-semibold text-foreground text-sm flex items-center gap-2"><Wrench size={15} className="text-amber-600" /> Rebalance Unbalanced Entries</h3>
                <p className="text-xs text-muted-foreground mt-1">Fixes journal entries whose debits ≠ credits when it's unambiguous (one line on the short side) — e.g. recruitment-fee cash legs that were short by the VAT. Ambiguous entries are left for manual review.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => runRebalance(false)} disabled={rbLoading} data-testid="rebalance-preview-btn">
                  <RefreshCw size={14} className={`mr-1 ${rbLoading ? 'animate-spin' : ''}`} /> Preview
                </Button>
                <Button size="sm" onClick={() => runRebalance(true)} disabled={rbLoading || !rebalance || (rebalance.entries_to_fix === 0 && !rebalance.applied)} data-testid="rebalance-apply-btn" className="bg-amber-600 hover:bg-amber-700">
                  Apply Rebalance
                </Button>
              </div>
            </div>
            {rebalance && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="rebalance-result">
                {rebalance.applied ? `Rebalanced ${rebalance.entries_fixed} entries.` : `${rebalance.entries_to_fix} entries to rebalance.`} {rebalance.entries_skipped_ambiguous} skipped (manual review).
              </p>
            )}
          </CardContent>
        </Card>

        {loading && !report ? (
          <Card><CardContent className="p-10 text-center text-muted-foreground text-sm">Running platform-wide checks…</CardContent></Card>
        ) : report ? (
          <>
            {/* Generated at */}
            <p className="text-[11px] text-muted-foreground text-right tabular-nums">
              Generated: {new Date(report.generated_at).toLocaleString()}
            </p>

            {/* Grid of sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Backend */}
              <Section title="Backend Process" icon={Activity} status={s.backend?.status} testid="section-backend">
                <dl className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                  <div><dt className="text-muted-foreground uppercase text-[10px]">Uptime</dt>
                       <dd className="font-bold text-foreground tabular-nums" data-testid="backend-uptime">{fmtSec(s.backend?.uptime_sec)}</dd></div>
                  <div><dt className="text-muted-foreground uppercase text-[10px]">Memory</dt>
                       <dd className={`font-bold tabular-nums ${s.backend?.memory_mb > 800 ? 'text-warning' : 'text-foreground'}`} data-testid="backend-memory">{s.backend?.memory_mb} MB</dd></div>
                  <div><dt className="text-muted-foreground uppercase text-[10px]">PID</dt>
                       <dd className="font-mono text-[11px] text-foreground opacity-70" data-testid="backend-pid">{s.backend?.pid}</dd></div>
                </dl>
              </Section>

              {/* Cold start */}
              <Section title="Cold-start History" icon={Clock} status={s.cold_start?.status} testid="section-cold-start">
                <dl className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                  <div><dt className="text-muted-foreground uppercase text-[10px]">Latest</dt>
                       <dd className="font-bold text-foreground tabular-nums" data-testid="cold-latest">{fmtMs(s.cold_start?.latest_ms)}</dd></div>
                  <div><dt className="text-muted-foreground uppercase text-[10px]">Average</dt>
                       <dd className="font-bold text-foreground tabular-nums">{fmtMs(s.cold_start?.avg_ms)}</dd></div>
                  <div><dt className="text-muted-foreground uppercase text-[10px]">Max</dt>
                       <dd className={`font-bold tabular-nums ${s.cold_start?.max_ms > 10000 ? 'text-warning' : 'text-foreground'}`}>{fmtMs(s.cold_start?.max_ms)}</dd></div>
                  <div><dt className="text-muted-foreground uppercase text-[10px]">Samples</dt>
                       <dd className="font-bold text-foreground tabular-nums">{s.cold_start?.count || 0}</dd></div>
                </dl>
              </Section>

              {/* Suppliers */}
              <Section title="Supplier Balances" icon={Users} status={s.suppliers?.status} testid="section-suppliers">
                <dl className="grid grid-cols-2 gap-2 text-xs">
                  <div><dt className="text-muted-foreground uppercase text-[10px]">Outstanding</dt>
                       <dd className="font-bold text-foreground tabular-nums" data-testid="suppliers-count">{fmtNum(s.suppliers?.outstanding_count)}</dd></div>
                  <div><dt className="text-muted-foreground uppercase text-[10px]">Total Exposure</dt>
                       <dd className="font-bold text-foreground tabular-nums" data-testid="suppliers-total">{fmtSR(s.suppliers?.total_outstanding)}</dd></div>
                </dl>
              </Section>

              {/* Journal Entries */}
              <Section title="Journal Entries Integrity" icon={BookOpen} status={s.journal_entries?.status} testid="section-journal">
                <dl className="grid grid-cols-1 gap-2 text-xs">
                  <div>
                    <dt className="text-muted-foreground uppercase text-[10px]">Unbalanced Entries</dt>
                    <dd className={`font-bold tabular-nums ${s.journal_entries?.unbalanced_count ? 'text-error' : 'text-success'}`} data-testid="je-unbalanced">
                      {fmtNum(s.journal_entries?.unbalanced_count)}
                    </dd>
                  </div>
                  {s.journal_entries?.sample?.length > 0 && (
                    <div className="text-[11px] text-muted-foreground pt-1 space-y-0.5">
                      {s.journal_entries.sample.map((e, i) => (
                        <div key={i} className="flex justify-between">
                          <span className="font-mono">{e.entry_number}</span>
                          <span className="text-error tabular-nums">diff {fmtSR(e.diff)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </dl>
              </Section>
            </div>

            {/* Collections table */}
            <Section title="Top Collections by Doc Count" icon={Database} status={s.collections?.status} testid="section-collections">
              <p className="text-[11px] text-muted-foreground mb-2">
                {s.collections?.total_collections} collections total
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="collections-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="text-left p-1.5">Collection</th>
                      <th className="text-right p-1.5">Documents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(s.collections?.top_15 || []).map((c, i) => (
                      <tr key={c.collection} className="border-b border-border/30" data-testid={`collection-row-${i}`}>
                        <td className="p-1.5 font-mono text-foreground">{c.collection}</td>
                        <td className="p-1.5 text-right tabular-nums font-semibold">{fmtNum(c.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* Legend */}
            <Card><CardContent className="p-3 text-[11px] text-muted-foreground flex flex-wrap items-center gap-4" data-testid="legend">
              <Layers size={12} />
              <span>Refreshes on-demand only (no polling). Run after every deploy to audit the whole platform in &lt;1s.</span>
            </CardContent></Card>
          </>
        ) : (
          <Card><CardContent className="p-10 text-center text-error text-sm">Failed to load report.</CardContent></Card>
        )}
      </div>
    </DashboardLayout>
  );
}
